import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import styled from 'styled-components';
import { shouldForwardProp } from '../../utils/styled-helpers';
import {
  getConnectionState,
  subscribeConnection,
  type ConnectionState,
} from '../../services/auth/refreshCoordinator';

// 스타일드 컴포넌트
const AlertContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isVisible',
})<{ isVisible: boolean }>`
  /* 2026-10-14 워크스페이스 디자인 — 흰 카드 + 호박색 점 (빨간 바탕 경고는 «고장» 처럼 읽혔다) */
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 320px;
  padding: 16px 18px;
  background-color: var(--ws-surface);
  border: 1px solid var(--ws-line);
  border-radius: var(--ws-r-xl);
  box-shadow: var(--ws-shadow-lg);
  color: var(--ws-ink-2);
  z-index: 1000;
  transform: translateY(${(props) => (props.isVisible ? '0' : '150%')});
  transition: transform 0.3s ease;
`;

const AlertTitle = styled.h4`
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 650;
  color: var(--ws-ink);
  display: flex;
  align-items: center;
  gap: 8px;
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--ws-amber);
    box-shadow: 0 0 0 3px var(--ws-amber-bg);
  }
`;

const AlertText = styled.p`
  margin: 0 0 14px;
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--ws-ink-3);
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const Button = styled.button`
  height: 34px;
  padding: 0 14px;
  border: 1px solid var(--ws-line);
  border-radius: 9px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 550;
  transition: background-color 0.15s, border-color 0.15s;
`;

const ExtendButton = styled(Button)`
  order: 2;
  background-color: var(--ws-primary);
  border-color: var(--ws-primary);
  color: var(--ws-ink-inv);
  box-shadow: var(--ws-shadow-primary);

  &:hover {
    background-color: var(--ws-primary-hover);
  }
`;

const LogoutButton = styled(Button)`
  order: 1;
  background-color: var(--ws-surface);
  color: var(--ws-ink-2);

  &:hover {
    background-color: var(--ws-subtle);
  }
`;

const TimeRemaining = styled.div`
  font-family: var(--ws-mono);
  font-weight: 600;
  margin-bottom: 14px;
  font-size: 13px;
  color: var(--ws-amber-ink);
`;

interface SessionTimeoutAlertProps {
  warningMinutes?: number;
}

const SessionTimeoutAlert: React.FC<SessionTimeoutAlertProps> = ({ warningMinutes = 5 }) => {
  const { state, extendSession, logout } = useAuth();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);

  // 외부 고객 격리 라우트(/track*, /homestyling-apply, /repair-apply, /agree)에서는 일반 사용자 세션 경고를 절대 노출하지 않는다.
  const isCustomerTrackingRoute =
    location.pathname.startsWith('/track') || location.pathname.startsWith('/homestyling-apply') ||
    location.pathname.startsWith('/repair-apply') || location.pathname.startsWith('/agree');

  // ⛔ 세션이 없는 화면에는 아무것도 띄우지 않는다 — 최종 심사 [F-8].
  //    이 부품은 App 전역에 항상 붙어 있어서, 조건을 두지 않으면
  //    로그인·비밀번호 재설정·인쇄 화면에까지 「서버 연결 안내」가 뜬다.
  const 세션없는화면 =
    ['/login', '/register', '/forgot-password', '/reset-password'].includes(location.pathname) ||
    location.pathname.startsWith('/print');

  const [연결, set연결] = useState<ConnectionState>(getConnectionState());

  useEffect(() => subscribeConnection(set연결), []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    // ⛔ 종전에는 이 setTimeout 을 정리하지 않았다. 세션이 갱신돼 effect 가 다시 돌아도
    //    옛 타이머가 살아남아 **지난 만료시각 기준으로** 경고를 띄웠다.
    let warningId: NodeJS.Timeout | null = null;

    // 세션 만료 경고 타이머 설정
    const setSessionWarning = () => {
      if (!state.sessionExpiry) return;

      const sessionExpiry = new Date(state.sessionExpiry);
      const warningTime = new Date(sessionExpiry.getTime() - warningMinutes * 60 * 1000);
      const now = new Date();

      // 경고 시간까지 타이머 설정
      if (now < warningTime) {
        const timeToWarning = warningTime.getTime() - now.getTime();
        warningId = setTimeout(() => {
          setIsVisible(true);
          startCountdown();
        }, timeToWarning);
      }
      // 이미 경고 시간을 지났지만 아직 만료되지 않은 경우
      else if (now < sessionExpiry) {
        setIsVisible(true);
        startCountdown();
      }
    };

    // 남은 시간 카운트다운
    const startCountdown = () => {
      if (!state.sessionExpiry) return;

      const sessionExpiry = new Date(state.sessionExpiry);

      // 1초마다 남은 시간 업데이트
      intervalId = setInterval(() => {
        const now = new Date();
        const diff = Math.max(0, sessionExpiry.getTime() - now.getTime());

        if (diff <= 0) {
          // ⛔ 여기서 `logout()` 을 부르지 않는다 [부당한 로그아웃 제거 1단계].
          //
          //    종전에는 이 부품이 **스스로** 로그아웃시켰다. 그래서 만료 판정이
          //    AuthContext 와 여기, **두 곳**에 있었다. 한 곳만 고치면 다른 곳이 뒤집는다.
          //    이제 만료 판정과 재발급 시도는 AuthContext 한 곳만 한다.
          //    이 부품은 **남은 시간을 보여 주는 일**만 한다.
          clearInterval(intervalId!);
          setTimeLeft(0);
        } else {
          setTimeLeft(diff);
        }
      }, 1000);
    };

    if (state.isAuthenticated && state.sessionExpiry) {
      setSessionWarning();
    } else {
      setIsVisible(false);
      setTimeLeft(null);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (warningId) clearTimeout(warningId);
    };
  }, [state.isAuthenticated, state.sessionExpiry, warningMinutes]);

  // 세션 연장 처리
  const handleExtendSession = async () => {
    try {
      await extendSession();
      setIsVisible(false);
      setTimeLeft(null);
    } catch (error) {
      // 서버 사정이면 조정자가 뒤에서 다시 시도한다. 여기서는 표시만 유지한다.
      console.error('세션 연장 실패:', error);
    }
  };

  // ⛔ 이미 만료된 뒤에는 연장을 누를 수 없게 한다.
  //    종전에는 이 부품이 0초에 스스로 로그아웃시켜 이 상황이 없었다.
  //    그 로그아웃을 걷어냈으므로, 만료 후 연장이 눌리는 것을 여기서 막는다.
  const 연장불가 = timeLeft !== null && timeLeft <= 0;

  // 로그아웃 처리
  const handleLogout = () => {
    logout();
    setIsVisible(false);
  };

  // 남은 시간 포맷팅 (분:초)
  const formatTimeLeft = () => {
    if (timeLeft === null) return '';

    const minutes = Math.floor(timeLeft / 60000);
    const seconds = Math.floor((timeLeft % 60000) / 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (isCustomerTrackingRoute || 세션없는화면) return null;
  // 로그인하지 않은 상태에서는 연결 안내도 의미가 없다
  if (!state.isAuthenticated && 연결 === 'ok') return null;
  if (!state.isAuthenticated && !localStorage.getItem('auth_token')) return null;

  return (
    // ⛔ 연결이 끊긴 안내는 **만료가 가깝지 않아도** 보여야 한다.
    //    종전 조건(isVisible)만 쓰면 만료 직전에만 떠서, 한낮에 서버가 끊겼을 때
    //    사용자는 «요청이 그냥 안 되는» 상태를 이유도 모른 채 겪는다.
    <AlertContainer isVisible={isVisible || 연결 !== 'ok'}>
      <AlertTitle>{연결 === 'ok' ? '세션 만료 경고' : '서버 연결 안내'}</AlertTitle>
      {연결 === 'ok' && (
        <AlertText>로그인 세션이 곧 만료됩니다. 계속 사용하시려면 세션을 연장하세요.</AlertText>
      )}
      {timeLeft !== null && <TimeRemaining>남은 시간: {formatTimeLeft()}</TimeRemaining>}
      {연결 === 'retrying' && (
        <AlertText>서버 연결을 확인하는 중입니다. 잠시만 기다려 주세요.</AlertText>
      )}
      {연결 === 'gave-up' && (
        <AlertText>서버에 연결하지 못했습니다. 아래 「다시 로그인」을 눌러 주세요.</AlertText>
      )}
      <ButtonGroup>
        {/* ⛔ «포기» 상태에서는 연장을 눌러도 막혀 있다. 대신 빠져나올 문을 준다 — [F-3] */}
        {연결 === 'gave-up' ? (
          <ExtendButton onClick={handleLogout}>다시 로그인</ExtendButton>
        ) : (
          <ExtendButton onClick={handleExtendSession} disabled={연장불가}>
            세션 연장
          </ExtendButton>
        )}
        <LogoutButton onClick={handleLogout}>로그아웃</LogoutButton>
      </ButtonGroup>
    </AlertContainer>
  );
};

// displayName 속성 추가
SessionTimeoutAlert.displayName = 'SessionTimeoutAlert';

export default SessionTimeoutAlert;
